import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getGetResourceObjectQueryKey,
  getGetResourceQueryKey,
  updateResourceObject,
  createResourceObjectMonetaryValue,
  updateResourceObjectMonetaryValue,
} from "@/orval/gen"
import {
  EditResourceDetailsFormData,
  EditObjectField,
  UpdateValuationData,
} from "../types"

interface UseEditResourceDetailsLogicProps {
  resourceObjectId: number
  resourceObject: any
}

export function useEditResourceDetailsLogic({
  resourceObjectId,
  resourceObject,
}: UseEditResourceDetailsLogicProps) {
  const queryClient = useQueryClient()

  const [isEditResourceDetailsModalOpen, setIsEditResourceDetailsModalOpen] =
    React.useState(false)
  const [isEditObjectFieldsModalOpen, setIsEditObjectFieldsModalOpen] =
    React.useState(false)
  const [editFormData, setEditFormData] =
    React.useState<EditResourceDetailsFormData>({
      name: resourceObject?.name ?? "",
      identificationCode: resourceObject?.identificationCode ?? "",
      conditionId: null,
      valuation: undefined,
    })
  const [editObjectFields, setEditObjectFields] = React.useState<
    EditObjectField[]
  >(
    resourceObject?.objectFields?.map((f: any) => ({
      type: f.type ?? {},
      textValue: f.textValue,
    })) ?? []
  )

  React.useEffect(() => {
    if (isEditResourceDetailsModalOpen && resourceObject) {
      setEditFormData({
        name: resourceObject.name ?? "",
        identificationCode: resourceObject.identificationCode ?? "",
        conditionId: null,
        valuation: undefined,
      })
    }
  }, [isEditResourceDetailsModalOpen, resourceObject])

  React.useEffect(() => {
    if (isEditObjectFieldsModalOpen && resourceObject?.objectFields) {
      setEditObjectFields(
        resourceObject.objectFields.map((f: any) => ({
          type: f.type ?? {},
          textValue: f.textValue,
        }))
      )
    }
  }, [isEditObjectFieldsModalOpen, resourceObject])

  const updateResourceObjectMutation = useMutation({
    mutationFn: async (data: EditResourceDetailsFormData) => {
      const payload = {
        resourceObjectId,
        name: data.name,
        identificationCode: data.identificationCode,
        resourceObjectConditionId: data.conditionId,
        purchasePrice: data.valuation,
      }

      const response = await fetch("/api/ResourceObject/UpdateResourceObject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error("Failed to update resource object")
      }
      return await response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetResourceObjectQueryKey({ resourceObjectId }),
      })
      if (resourceObject?.resourceId) {
        queryClient.invalidateQueries({
          queryKey: getGetResourceQueryKey({
            resourceId: resourceObject.resourceId,
          }),
        })
      }
      setIsEditResourceDetailsModalOpen(false)
    },
  })

  const createValuationMutation = useMutation({
    mutationFn: async (monetaryValue: number) => {
      const payload = {
        resourceObjectId,
        monetaryValue,
        estimateDate: new Date().toISOString().split("T")[0],
        valuation: null,
        estimatedByPersonId: null,
      }

      return createResourceObjectMonetaryValue(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectMonetaryValues", resourceObjectId],
      })
    },
  })

  const updateValuationMutation = useMutation({
    mutationFn: async (data: UpdateValuationData) => {
      const payload = {
        resourceObjectMonetaryValueId: data.resourceObjectMonetaryValueId,
        resourceObjectId,
        monetaryValue: data.monetaryValue,
        estimateDate: new Date().toISOString().split("T")[0],
        valuation: null,
        estimatedByPersonId: null,
      }

      return updateResourceObjectMonetaryValue(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectMonetaryValues", resourceObjectId],
      })
    },
  })

  const updateObjectFieldsMutation = useMutation({
    mutationFn: async (fields: EditObjectField[]) => {
      const payload = {
        resourceObjectId,
        objectFields: fields.map((f) => ({
          type: f.type,
          textValue: f.textValue,
        })),
      }

      const response = await fetch(
        "/api/ResourceObject/UpdateResourceObjectFields",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        throw new Error("Failed to update object fields")
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetResourceObjectQueryKey({ resourceObjectId }),
      })
      setIsEditObjectFieldsModalOpen(false)
    },
  })

  return {
    isEditResourceDetailsModalOpen,
    setIsEditResourceDetailsModalOpen,
    isEditObjectFieldsModalOpen,
    setIsEditObjectFieldsModalOpen,
    editFormData,
    setEditFormData,
    editObjectFields,
    setEditObjectFields,
    updateResourceObjectMutation,
    updateObjectFieldsMutation,
    createValuationMutation,
    updateValuationMutation,
  }
}
